import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

/**
 * Mirrors backend utils/pricingPermissions.js logic on the frontend.
 *
 * Roles that ALWAYS see pricing (hardcoded in backend):
 *   - admin
 *   - super_admin
 *
 * Other roles: configurable via companies.settings.view_pricing JSON.
 *   - csr           default: true
 *   - manager       default: false
 *   - supervisor    default: false
 *   - router        default: false
 *   - technician    default: false
 *
 * The role 'customer_service' is normalized to 'csr'.
 */
const ALWAYS_VIEW_ROLES = new Set(['admin', 'super_admin']);

function normalizeRole(role) {
  if (!role) return null;
  if (role === 'customer_service') return 'csr';
  return role;
}

// Module-level cache so the settings are fetched only once per session.
// Cleared on user change (login/logout) via the effect below.
let _settingsCache = null;
let _settingsCacheUserId = null;
let _settingsInflight = null; // dedupe concurrent fetches

async function loadSettings(userId) {
  if (_settingsCache && _settingsCacheUserId === userId) {
    return _settingsCache;
  }
  if (_settingsInflight) {
    return _settingsInflight;
  }
  _settingsInflight = (async () => {
    try {
      const res = await api.get('/companies/settings');
      const data = res.data && res.data.data ? res.data.data : res.data;
      // data may be the full company row; settings is under .settings
      const settings = (data && data.settings) || {};
      _settingsCache = settings;
      _settingsCacheUserId = userId;
      return settings;
    } catch (err) {
      console.warn('[useUserPermissions] settings fetch failed:', err.message);
      return null;
    } finally {
      _settingsInflight = null;
    }
  })();
  return _settingsInflight;
}

/**
 * Public: clear cache (call on logout if you want to force refresh).
 */
export function clearPermissionsCache() {
  _settingsCache = null;
  _settingsCacheUserId = null;
  _settingsInflight = null;
}

/**
 * Hook: returns pricing permissions and other role-based helpers.
 */
export function useUserPermissions() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(_settingsCache);
  const [loading, setLoading] = useState(!_settingsCache);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setSettings(null);
      setLoading(false);
      _settingsCache = null;
      _settingsCacheUserId = null;
      return;
    }
    if (_settingsCache && _settingsCacheUserId === user.id) {
      setSettings(_settingsCache);
      setLoading(false);
      return;
    }
    setLoading(true);
    loadSettings(user.id).then(s => {
      if (!cancelled) {
        setSettings(s);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [user]);

  // ── Computed permissions ────────────────────────────────────────────────
  const canViewPricing = (() => {
    if (!user || !user.role) return false;
    if (ALWAYS_VIEW_ROLES.has(user.role)) return true;
    const role = normalizeRole(user.role);
    if (!role) return false;
    const cfg = settings && settings.view_pricing;
    if (!cfg || typeof cfg !== 'object') {
      // Same fallback as backend
      return role === 'csr';
    }
    return cfg[role] === true;
  })();

  // Convenience: refetch settings (call after PUT /settings to refresh)
  const refreshSettings = useCallback(async () => {
    if (!user) return null;
    clearPermissionsCache();
    setLoading(true);
    const s = await loadSettings(user.id);
    setSettings(s);
    setLoading(false);
    return s;
  }, [user]);

  return {
    canViewPricing,
    settings,
    loading,
    refreshSettings,
    // Helper for UI: returns a list of [role, canView] pairs (used by the
    // Settings page to render the toggles).
    getPricingMatrix: () => {
      const cfg = (settings && settings.view_pricing) || {};
      return [
        { role: 'csr',        label: 'Customer Service',  canView: cfg.csr !== undefined ? cfg.csr : true },
        { role: 'manager',    label: 'Manager',           canView: cfg.manager === true },
        { role: 'supervisor', label: 'Supervisor',        canView: cfg.supervisor === true },
        { role: 'router',     label: 'Router',            canView: cfg.router === true },
        { role: 'technician', label: 'Technician',        canView: cfg.technician === true },
      ];
    },
  };
}

export default useUserPermissions;
